import { useNavigate, useSearchParams } from "@solidjs/router";
import { type ViewId, ViewIdSchema } from "@storytell/types";
import lzString from "lz-string";
import { createEffect, createMemo, createSignal } from "solid-js";
import { isServer } from "solid-js/web";

export type KnowledgeUIService = ReturnType<typeof createKnowledgeUIService>;

export const createKnowledgeUIService = () => {
  const nav = useUrlParamNavigation();

  return {
    ...nav,
  };
};

const constViews = ["all", "new"] as const;

type KnowledgeViewType = (typeof constViews)[number] | ViewId;

const encodePath = (path: string[]) => lzString.compressToEncodedURIComponent(path.join("/"));

const useUrlParamNavigation = () => {
  const mainParam = "kpp";
  const navigate = useNavigate();

  const defaultTab = "assets" as const;
  const defaultView = "all" as const;

  const [lastPath, setLastPath] = createSignal<string[]>([defaultView, defaultTab]);

  const decodePath = (path: string): [KnowledgeViewType, "concepts" | "assets"] | undefined => {
    const decoded = lzString.decompressFromEncodedURIComponent(path).split("/");
    if (!Array.isArray(decoded) || decoded.length === 0) return undefined;
    if (!constViews.includes(decoded[0] as "all" | "new") && !ViewIdSchema.safeParse(decoded[0]).success)
      return undefined;

    const view = decoded[0] as KnowledgeViewType;
    if (decoded.length === 1) return [view, defaultTab];
    if (decoded[1] !== "concepts" && decoded[1] !== "assets") return [view, defaultTab];
    return decoded as [KnowledgeViewType, "concepts" | "assets"];
  };

  const [params] = useSearchParams();
  const path = () => {
    const encoded = params[mainParam];
    if (typeof encoded !== "string") return undefined;
    return decodePath(encoded);
  };

  if (!isServer) {
    createEffect(() => {
      const current = path();
      if (current !== undefined) {
        setLastPath(current);
      }
    });
  }

  const activeView = createMemo(() => path()?.[0]);

  const isOpen = () => path() !== undefined;

  const open = () => {
    if (!isServer) {
      const app = document.getElementById("app");
      if (app) {
        const focusedElement = app.querySelector(":focus") as HTMLElement | null;
        if (focusedElement) {
          focusedElement.blur();
        }
        if (document.activeElement && app.contains(document.activeElement)) {
          (document.activeElement as HTMLElement).blur();
        }
      }
    }

    const url = new URL(window.location.href);
    url.searchParams.set(mainParam, encodePath(lastPath()));
    const newUrl = url.pathname + url.search + url.hash;
    navigate(newUrl);
  };

  const close = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(mainParam);
    const newUrl = url.pathname + url.search + url.hash;
    navigate(newUrl);
  };

  return {
    activeView,
    isOpen,
    open,
    close,
  };
};
